import React from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
  getMemberOf,
  setViewChurchSearchModal,
} from "features/memberships/membershipSlice";
import { useRef, useEffect } from "react";
import useToggleDropdown from "hooks/useToggleDropdown";
import Spinner from "loaders/Spinner";
import {
  setErrorField,
  setCreatePostPostingTo,
} from "features/posts/postSlice";
import { TbBuildingChurch } from "react-icons/tb";
import { MdOutlineSearchOff } from "react-icons/md";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";

export default function FormTop() {
  const { t } = useTranslation("feed");
  const dispatch = useDispatch();
  const ref1 = useRef(null);
  const [isDropped, setIsDropped] = useToggleDropdown(ref1);

  const { user } = useSelector((state) => state.auth);
  const { postType, isAnonymous } = useSelector((state) => state.postType);
  const { memberOf, isMbsLoading, isMbsSuccess } = useSelector(
    (state) => state.memberships2,
  );
  const { errorField, createPostPostingTo } = useSelector(
    (state) => state.posts,
  );

  const hasNoChurch = isMbsSuccess && memberOf && memberOf.length === 0;

  const handleGetMembers = () => {
    if (!isMbsSuccess) dispatch(getMemberOf());
  };

  useEffect(() => {
    if (memberOf && memberOf.length === 0) dispatch(getMemberOf());
  }, []);

  useEffect(() => {
    if (user.isOrganization) {
      dispatch(
        setCreatePostPostingTo({ fullName: user.fullName, _id: user._id }),
      );
    } else if (isMbsSuccess && memberOf && memberOf.length === 1) {
      dispatch(setCreatePostPostingTo(memberOf[0]));
    }
  }, [isMbsSuccess, memberOf]);

  const handlePostingToClick = () => {
    if (errorField === "postingTo") dispatch(setErrorField(""));
    if (hasNoChurch) {
      dispatch(setViewChurchSearchModal());
    } else {
      setIsDropped.toggle(ref1);
    }
  };

  const handleGroupClick = (value) => {
    dispatch(setCreatePostPostingTo(value));
    setIsDropped.close?.();
  };

  const avatarSrc = isAnonymous ? null : user.imagePath;

  return (
    <Container>
      {/* Avatar */}
      <AvatarWrap>
        {isAnonymous ? (
          <AnonCircle>?</AnonCircle>
        ) : (
          <Avatar src={avatarSrc} alt="" />
        )}
      </AvatarWrap>

      {/* Name + church selector */}
      <Meta>
        <UserName>
          {isAnonymous ? <em>{t("anonymous")}</em> : user.fullName}
        </UserName>

        {!user.isOrganization && (
          <ChurchBtn
            ref={ref1}
            onClick={handlePostingToClick}
            onMouseEnter={handleGetMembers}
            isError={errorField === "postingTo"}
            hasChurch={!!createPostPostingTo?.fullName}
          >
            <TbBuildingChurch style={{ fontSize: "13px", flexShrink: 0 }} />
            <ChurchBtnText>
              {isMbsLoading
                ? t("loading")
                : hasNoChurch
                  ? t("composer.findChurch")
                  : createPostPostingTo?.fullName
                    ? `${createPostPostingTo.fullName}`
                    : t("composer.selectDestination")}
            </ChurchBtnText>
            <KeyboardArrowDownIcon
              style={{ fontSize: "14px", flexShrink: 0 }}
            />

            {isDropped && !hasNoChurch && (
              <Dropdown>
                {isMbsLoading ? (
                  <DropdownItem>
                    <Spinner />
                  </DropdownItem>
                ) : memberOf && memberOf.length > 0 ? (
                  memberOf.map((ele) => (
                    <DropdownItem
                      key={ele._id}
                      onClick={() => handleGroupClick(ele)}
                    >
                      {ele.profileImg?.url && (
                        <img
                          src={ele.profileImg.url}
                          alt=""
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      )}
                      <span>{ele.fullName}</span>
                    </DropdownItem>
                  ))
                ) : (
                  <DropdownItem disabled>
                    {t("composer.noChurches")}
                  </DropdownItem>
                )}
              </Dropdown>
            )}
          </ChurchBtn>
        )}
      </Meta>

      {/* Post type badge (right side) */}
      {postType && (
        <PostTypeBadge type={postType}>
          {postType === "Prayer Request"
            ? `🙏 ${t("contentTypes.prayer")}`
            : `✏️ ${t("contentTypes.post")}`}
        </PostTypeBadge>
      )}
    </Container>
  );
}

/* ── Styled components ─────────────────────────────────────────────────────── */

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  padding: 16px 20px 12px;
  gap: 12px;
`;

const AvatarWrap = styled.div`
  flex-shrink: 0;
`;

const Avatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  background-color: #ddd;
`;

const AnonCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #fff;
  font-weight: 700;
`;

const Meta = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--lightThemeFontColor);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChurchBtn = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid
    ${(p) => (p.isError ? "#f44336" : p.hasChurch ? "var(--themeColor)" : "#d0d0d0")};
  background-color: ${(p) => (p.hasChurch ? "rgba(0,180,204,0.07)" : "#f8f8f8")};
  cursor: pointer;
  font-size: 12px;
  color: ${(p) => (p.isError ? "#f44336" : p.hasChurch ? "var(--themeColor)" : "var(--lightThemeFontColor)")};
  position: relative;
  max-width: 260px;
  transition:
    border-color 0.15s,
    background 0.15s;

  &:hover {
    background-color: ${(p) => (p.hasChurch ? "rgba(0,180,204,0.12)" : "#f0f0f0")};
  }
`;

const ChurchBtnText = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  border: 1px solid #eee;
  min-width: 200px;
  max-width: 260px;
  z-index: 20;
  overflow: hidden;
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: var(--lightThemeFontColor);
  cursor: ${(p) => (p.disabled ? "default" : "pointer")};
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};

  &:hover {
    background-color: ${(p) => (p.disabled ? "transparent" : "#f4f4f4")};
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const PostTypeBadge = styled.div`
  flex-shrink: 0;
  padding: 5px 11px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${(p) => (p.type === "Prayer Request" ? "rgba(76,175,80,0.12)" : "rgba(0,180,204,0.10)")};
  color: ${(p) => (p.type === "Prayer Request" ? "#388e3c" : "var(--themeColor)")};
`;
